import { useContext, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Icon,
  Paragraph,
  Text,
  Toast,
  Tooltip,
  useToast,
} from '@zillow/constellation-10';
import { IconTrophyFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany,
  useRentalManagerUserPropertySummaryWebApiHandler_applications,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../../actions/PropertiesActions';
import { RMWContext } from '../../../../providers/RMWContext';
import { RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import ApplicationsCSEvents from '../../../../model/ClickstreamEvent/ApplicationsCSEvents';

import {
  EmptyStateBodyText,
  EmptyStateTitleText,
  StyledEmptyStateContainer,
  StyledIconContainer,
} from '../../../../styles';

import AlertWithTitle from '../../../AlertWithTitle';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import ListingActivationTerm from '../../../ListingActivationTerms';
import SimpleCard from '../../../SimpleCard';
import ApplicationShareLinks from '../ApplicationShareLinks';

import type React from 'react';
import type { TRIGGER_LOCATIONS } from '../../../../constants/ClickstreamConstants';
import type { ApplicationsSummary } from '../../interfaces';

type Props = {
  canEnableApplications: boolean;
  displayType?: VERTICAL_DISPLAY_TYPE;
  setShowAppsPermissionModal: (value: boolean) => void;
  applicationsSummary?: ApplicationsSummary;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
  onSetApplicationManagerClick?: () => void;
};

const accessText =
  'You need permission to enable and access applications. Request access from your account’s application manager.';

const ApplicationsDisabledCard = ({
  canEnableApplications,
  triggerLocation,
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  setShowAppsPermissionModal,
  applicationsSummary,
  onSetApplicationManagerClick,
}: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    paths: { tenantScreeningPage },
  } = useContext(RMWContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const { data: isApplicationManager } =
    useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany();

  const { canAccessBusinessVerificationLite, listingAlias, status, user } = useSelector(
    (state) => ({
      canAccessBusinessVerificationLite: state?.user?.features?.canAccessBusinessVerificationLite,
      listingAlias: state?.properties?.propertyOverview?.listingAlias,
      status: state?.properties?.propertyOverview?.status,
      user: state?.user,
    }),
    shallowEqual,
  );

  const [isEnableApplicationsLoading, setIsEnableApplicationsLoading] = useState(false);
  const [failureBanner, setFailureBanner] = useState<{ body: React.ReactNode | null; title: string | null }>({
    body: null,
    title: null,
  });

  const { refetch: refetchGetNextStepsSummary } = useRentalManagerUserPropertySummaryWebApiHandler_nextSteps(
    { listingAlias },
    { enabled: false, staleTime: TimeConstants.SECONDS_10 },
  );
  const { refetch: refetchWebAnalytics } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: Boolean(listingAlias) },
  );

  const displayIconTrophy = displayType === VERTICAL_DISPLAY_TYPE.FULL;
  const loadingButtonSize = displayType === VERTICAL_DISPLAY_TYPE.FULL ? 'md' : 'sm';

  const title = 'Simplify your rental process';

  const handleEnableApplicationsClick = () => {
    if (canAccessBusinessVerificationLite && !isApplicationManager && user.isPropertyManagerCompanyAdmin) {
      setShowAppsPermissionModal(true);
    } else {
      setIsEnableApplicationsLoading(true);

      dispatch(PropertiesActions.isApplicationsAccepted(listingId))
        // @ts-expect-error
        .then(() => {
          refetchGetNextStepsSummary();
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias: listingId }),
          });
          enqueueToast(<Toast tone="success">Applications have been enabled on your listing</Toast>);

          refetchWebAnalytics()
            .then((response) => {
              ApplicationsCSEvents.ClickEnableApplications({
                currentProperty: response.data ?? {},
                user,
                triggerLocation,
              });
            })
            .catch((error) =>
              logError({
                error,
                context: 'ApplicationsDisabledCard',
              }),
            );
        })
        .catch(() => {
          setFailureBanner({
            title: null,
            body: 'There was an error enabling applications.',
          });
        })
        .finally(() => setIsEnableApplicationsLoading(false));
    }
  };

  const renderBodyContent = useMemo(() => {
    return (
      <>
        Save time with Zillow&apos;s online rental applications. Access credit reports, background checks and eviction
        history in one place.{' '}
        <Anchor href={tenantScreeningPage} target="_blank">
          Learn more
        </Anchor>
      </>
    );
  }, []);

  const renderEnableApplicationsButton = useMemo(() => {
    return (
      <Button
        data-testid="EnableApplicationsButton"
        tone="brand"
        // status is an enum value — do not change the string comparison
        // eslint-disable-next-line constellation/word-list
        emphasis={status.toLowerCase() === 'off-market' ? 'outlined' : 'filled'}
        size={loadingButtonSize}
        onClick={handleEnableApplicationsClick}
        disabled={!canEnableApplications}
        loading={isEnableApplicationsLoading}
        data-eventid={ApplicationsCSEvents.ClickEnableApplications.data_event_id}
      >
        Enable applications
      </Button>
    );
  }, [isApplicationManager]);

  const renderTerms = useMemo(() => {
    return (
      <div data-testid="Terms-Container">
        {canEnableApplications ? (
          <ListingActivationTerm actionText="By enabling or sharing applications" />
        ) : (
          <Paragraph textStyle="body-sm" css={{ marginTop: 'default' }}>
            Why can&apos;t I enable applications <Tooltip content={accessText} placement="top" />
          </Paragraph>
        )}
      </div>
    );
  }, []);

  const renderFullType = useMemo(() => {
    return (
      <ConnectedModuleCard data-testid="ApplicationsDisabledCard-Full">
        <StyledEmptyStateContainer>
          <StyledIconContainer aria-hidden="true">
            {displayIconTrophy && <Icon size="md" css={{ color: 'icon.subtle' }} render={<IconTrophyFilled />} />}
          </StyledIconContainer>

          <EmptyStateTitleText asChild>
            <h4>{title}</h4>
          </EmptyStateTitleText>

          <EmptyStateBodyText asChild>
            <p>{renderBodyContent}</p>
          </EmptyStateBodyText>

          <ButtonGroup
            data-testid="CTAContainer"
            aria-label="Application actions"
            orientation="horizontal"
            css={{ alignItems: 'center' }}
          >
            {renderEnableApplicationsButton}

            {(canAccessBusinessVerificationLite ? isApplicationManager : true) && (
              <ApplicationShareLinks
                displayType={displayType}
                isTertiaryButtonStyling={true}
                applicationsSummary={applicationsSummary}
                hideViewOlderApplications={false}
                onSetApplicationManagerClick={onSetApplicationManagerClick}
              />
            )}
          </ButtonGroup>

          <Box css={{ marginBottom: 'default' }} />
          {renderTerms}
        </StyledEmptyStateContainer>
      </ConnectedModuleCard>
    );
  }, [renderEnableApplicationsButton, renderTerms]);

  const renderSimpleType = useMemo(() => {
    return (
      <SimpleCard data-testid="ApplicationsDisabledCard-Simple">
        <Text asChild textStyle="body-bold">
          <p>{title}</p>
        </Text>

        <Text asChild textStyle="body-sm" css={{ marginBottom: 'tight' }}>
          <p>{renderBodyContent}</p>
        </Text>

        <ButtonGroup
          data-testid="CTAContainer"
          aria-label="Application actions"
          orientation="horizontal"
          css={{ alignItems: 'center' }}
        >
          {renderEnableApplicationsButton}

          {(canAccessBusinessVerificationLite ? isApplicationManager : true) && (
            <ApplicationShareLinks
              displayType={displayType}
              isTertiaryButtonStyling={true}
              applicationsSummary={applicationsSummary}
              hideViewOlderApplications={false}
              onSetApplicationManagerClick={onSetApplicationManagerClick}
            />
          )}
        </ButtonGroup>

        {renderTerms}
      </SimpleCard>
    );
  }, [renderEnableApplicationsButton, renderTerms]);

  if (failureBanner.body) {
    return <AlertWithTitle appearance="critical" body={failureBanner.body} title={failureBanner.title} />;
  }

  return (
    <div data-testid="ApplicationsDisabledCard">
      {displayType === VERTICAL_DISPLAY_TYPE.FULL ? renderFullType : renderSimpleType}
    </div>
  );
};

export default ApplicationsDisabledCard;
